import { IonPage, IonSpinner, useIonRouter } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { FormInput } from "@/components/atoms/form-ion/input";
import { TitleIcon } from "@/components/atoms/title-icon";
import { AuthContent } from "@/components/molecules/auth/content";
import { AuthHeader } from "@/components/molecules/auth/header";
import { AuthPageHeaderTitle } from "@/components/molecules/auth/page-header-title";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { env } from "@/env";
import { checkCircleIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { exclamationTriangleIcon, keyIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { authClient } from "@/utils/auth";

export function AuthForgotPasswordReset() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const searchParams = new URLSearchParams(router.routeInfo.search);
  const token = searchParams.get("token");

  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Use ref to track success immediately (synchronously) to prevent validation re-runs
  const isSuccessRef = useRef(false);

  // Validate token using React Query (disabled after password reset succeeds)
  const {
    data: tokenValidation,
    isLoading: isValidating,
    error: _validationError,
  } = useQuery({
    queryKey: ["validate-reset-token", token],
    queryFn: async () => {
      if (!token) {
        throw new Error("No token provided");
      }

      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/auth/validate-reset-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        },
      );

      if (!response.ok) {
        throw new Error("Token validation failed");
      }

      const data = (await response.json()) as {
        valid: boolean;
        email?: string;
        expiresAt?: string;
      };

      return data;
    },
    enabled: Boolean(token) && !isSuccess && !isSuccessRef.current,
    retry: false,
    staleTime: Infinity, // Token validation result never becomes stale
  });

  const isTokenValid = tokenValidation?.valid ?? false;
  const tokenEmail = tokenValidation?.email ?? "";

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    onSubmit: async (values) => {
      if (!token) {
        return;
      }

      try {
        const { error } = await authClient.resetPassword({
          newPassword: values.value.password,
          token,
        });

        if (error) {
          toast.error(t("routes:auth.reset-password.errors.failed"), {
            description: error.message,
          });
          return;
        }

        isSuccessRef.current = true;
        setIsSuccess(true);

        setTimeout(() => {
          history.replace(Routes.AuthSignInPassword());
        }, 2000);
      } catch (err) {
        toast.error(t("routes:auth.reset-password.errors.failed"));
      }
    },
  });

  // Show loading state while validating token
  if (isValidating) {
    return (
      <IonPage>
        <AuthHeader />
        <AuthContent>
          <div className="flex items-center justify-center">
            <IonSpinner />
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  // Show success state after password reset
  if (isSuccess) {
    return (
      <IonPage>
        <AuthHeader />
        <AuthContent>
          <TitleIcon icon={checkCircleIcon} className="text-success" />

          <AuthPageHeaderTitle
            parts={{
              title: { children: t("routes:auth.reset-password.success.title") },
              subtitle: {
                children: t("routes:auth.reset-password.success.subtitle"),
              },
            }}
          />

          <div className="flex items-center justify-center">
            <IonSpinner />
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  // Show expired/invalid token state
  if (!isTokenValid && !isSuccess) {
    return (
      <IonPage>
        <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
        <AuthContent>
          <TitleIcon icon={exclamationTriangleIcon} className="text-warning" />

          <AuthPageHeaderTitle
            parts={{
              title: { children: t("routes:auth.reset-password.expired.title") },
              subtitle: {
                children: t("routes:auth.reset-password.expired.subtitle"),
              },
            }}
          />

          <div className="flex flex-col gap-6">
            <AuthSubmitButton onClick={() => history.replace(Routes.AuthForgotPassword())}>
              {t("routes:auth.reset-password.expired.action.requestNew")}
            </AuthSubmitButton>
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
      <AuthContent>
        <TitleIcon icon={keyIcon} />

        <AuthPageHeaderTitle
          parts={{
            title: { children: t("page.auth.sign-up.steps.password.title") },
            subtitle: {
              children: t("page.auth.sign-up.steps.password.subtitle", {
                number: env.VITE_PASSWORD_MIN_LENGTH,
                requirement: env.VITE_PASSWORD_REQUIREMENTS,
              }),
            },
          }}
        >
          <ul className="ml-6 list-disc">
            <li>{t("fields.password.requirements.capital")}</li>
            <li>{t("fields.password.requirements.lowercase")}</li>
            <li>{t("fields.password.requirements.digit")}</li>
            <li>{t("fields.password.requirements.symbol")}</li>
          </ul>
        </AuthPageHeaderTitle>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-6"
        >
          <form.Field
            name="password"
            validators={{
              onSubmit: z
                .string()
                .min(
                  env.VITE_PASSWORD_MIN_LENGTH,
                  t("fields.password.errors.min", {
                    number: env.VITE_PASSWORD_MIN_LENGTH,
                  }),
                )
                .refine(
                  (value) => {
                    let requirementMet = 0;

                    // Uppercase
                    if (/[A-Z]/g.test(value)) {
                      requirementMet++;
                    }

                    // Lowercase
                    if (/[a-z]/g.test(value)) {
                      requirementMet++;
                    }

                    // Number
                    if (/[0-9]/g.test(value)) {
                      requirementMet++;
                    }

                    // Symbol
                    if (/[~`¿¡!#$%^&*€£@+÷=\-[\]\\';,/{}()|\\":<>?._]/.test(value)) {
                      requirementMet++;
                    }

                    return requirementMet >= env.VITE_PASSWORD_REQUIREMENTS;
                  },
                  {
                    message: t("fields.password.errors.requirement", {
                      requirement: env.VITE_PASSWORD_REQUIREMENTS,
                    }),
                  },
                ),
            }}
            children={(field) => (
              <div className="relative">
                <FormInput
                  field={field}
                  type={showPassword ? "text" : "password"}
                  autofocusOnIonViewDidEnter
                  label={t("fields.password.label")}
                  autocomplete="new-password"
                  placeholder={t("fields.password.placeholder")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={t(
                    showPassword ? "common:a11y.hide-password" : "common:a11y.show-password",
                  )}
                  className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            validators={{
              onSubmit: z
                .string()
                .min(1, t("fields:passwordConfirmation.error.required"))
                .refine(
                  (value) => value === form.state.values.password,
                  t("fields:passwordConfirmation.error.not-match"),
                ),
            }}
            children={(field) => (
              <div className="relative">
                <FormInput
                  field={field}
                  type={showConfirmPassword ? "text" : "password"}
                  label={t("fields:passwordConfirmation.label")}
                  autocomplete="new-password"
                  placeholder={t("fields:passwordConfirmation.placeholder")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={t(
                    showConfirmPassword ? "common:a11y.hide-password" : "common:a11y.show-password",
                  )}
                  className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]) => (
              <AuthSubmitButton disabled={!canSubmit} isSubmitting={isSubmitting}>
                {t("page.auth.sign-up.steps.password.action.primary")}
              </AuthSubmitButton>
            )}
          />
        </form>
      </AuthContent>
    </IonPage>
  );
}
